import { BufferAttribute, type Mesh, type MeshStandardMaterial } from 'three';

/**
 * Safely clone a material (handles arrays and missing clone method)
 */
export const cloneMaterial = (mat: unknown): MeshStandardMaterial | null => {
	if (!mat) return null;
	// If it's an array, use the last material (the textured one).
	// classifySurroundFaces sets material = [plain, textured], so mat[0] is
	// the untextured edge material. Picking the last element preserves the
	// surround texture across CSG regeneration cycles.
	const material = Array.isArray(mat) ? mat[mat.length - 1] : mat;
	if (material && typeof (material as MeshStandardMaterial).clone === 'function') {
		return (material as MeshStandardMaterial).clone() as MeshStandardMaterial;
	}
	return (material as MeshStandardMaterial) || null;
};

/**
 * Clone a material array, preserving multi-material setups (e.g. countertop
 * [cap, edge] materials). Returns the cloned array, or a single-element array
 * wrapping a cloned material if the input is not an array.
 */
export const cloneMaterialArray = (mat: unknown): Array<MeshStandardMaterial> | null => {
	if (!mat) return null;
	if (Array.isArray(mat)) {
		return mat.map((m: MeshStandardMaterial) =>
			typeof m.clone === 'function' ? m.clone() as MeshStandardMaterial : m
		);
	}
	const single = cloneMaterial(mat);
	return single ? [single] : null;
};

/**
 * Add color attribute to mesh geometry for CSG operations (required by three-csg-ts)
 */
export const addColorAttribute = (mesh: Mesh): void => {
	if (!mesh.geometry.attributes.color) {
		const positionCount = mesh.geometry.attributes.position.count;
		const colors = new Float32Array(positionCount * 3);
		for (let i = 0; i < colors.length; i++) {
			colors[i] = 1.0;
		}
		mesh.geometry.setAttribute('color', new BufferAttribute(colors, 3));
	}
};

/**
 * Reset material opacity to 1 and transparent to false
 * The original materials may have been mid-animation (opacity < 1, transparent = true)
 * when they were cloned, so the merged meshes would inherit those intermediate values.
 */
export const resetMaterialOpacity = (mat: MeshStandardMaterial): void => {
	mat.opacity = 1;
	mat.transparent = false;
	mat.needsUpdate = true;
};
